import React, { useEffect, useMemo, useState } from 'react';
import { Select, Spin, Typography, Space } from 'antd';
import { TagsOutlined } from '@ant-design/icons';
import tagService from '../../services/tagService';

const { Text } = Typography;
const { OptGroup, Option } = Select;

/**
 * 通用标签选择组件
 * - 按标签组分组展示
 * - 支持多选
 * @param {Object} props
 * @param {Array<number|string>|number|string} props.value 选中值（多选为数组，单选为单值）
 * @param {function} props.onChange 选项变更回调
 * @param {string} props.placeholder 占位文本
 * @param {boolean} props.disabled 是否禁用
 * @param {string|number} props.width 选择器宽度
 * @param {function} props.onTagMapReady 可选，返回 id->name 的映射
 * @param {number|string} props.maxTagCount 可选，控制已选项折叠数量，默认不折叠
 * @param {('multiple'|'single'|'tags')} props.mode 选择模式；默认 multiple，传 single 为单选
 */
const TagSelect = ({
  value,
  onChange,
  placeholder = '选择标签',
  disabled = false,
  width = '100%',
  onTagMapReady,
  maxTagCount,
  mode = 'multiple'
}) => {
  const [loading, setLoading] = useState(false);
  const [tags, setTags] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchTags = async () => {
      setLoading(true);
      try {
        const list = await tagService.getAllTags();
        const normalized = Array.isArray(list) ? list : [];
        setTags(normalized);

        // 构建映射回调
        if (typeof onTagMapReady === 'function') {
          const map = new Map();
          normalized.forEach(t => {
            const tagName = t.name || t.tagName || `ID:${t.id}`;
            map.set(t.id, tagName);
          });
          onTagMapReady(map);
        }
      } catch (e) {
        // 交给上层处理错误提示更合适，此处仅静默
        // console.error('加载标签失败', e);
      } finally {
        setLoading(false);
      }
    };

    fetchTags();
  }, []);

  // 分组：groupName -> tags
  const grouped = useMemo(() => {
    const groupMap = new Map();
    tags.forEach(tag => {
      const groupName = tag.groupName || '未分组';
      if (!groupMap.has(groupName)) groupMap.set(groupName, []);
      groupMap.get(groupName).push(tag);
    });
    return groupMap;
  }, [tags]);

  // 选项过滤（按名称或分组）
  const filterOption = (input, option) => {
    const label = (option?.label || '').toString().toLowerCase();
    const group = (option?.group || '').toString().toLowerCase();
    const term = (input || '').toLowerCase();
    return label.includes(term) || group.includes(term);
  };

  return (
    <Select
      mode={mode === 'multiple' || mode === 'tags' ? mode : undefined}
      value={mode === 'multiple' || mode === 'tags' ? (value || []) : (value ?? undefined)}
      onChange={onChange}
      placeholder={placeholder}
      style={{ width }}
      disabled={disabled}
      loading={loading}
      showSearch
      filterOption={filterOption}
      onSearch={setSearch}
      size="middle"
      maxTagCount={maxTagCount}
      notFoundContent={loading ? <Spin size="small" /> : '暂无标签'}
    >
      {Array.from(grouped.keys()).sort().map(groupName => (
        <OptGroup key={groupName} label={groupName}>
          {(grouped.get(groupName) || []).map(tag => {
            const tagName = tag.name || tag.tagName || '未知标签';
            return (
              <Option 
                key={tag.id}
                value={tag.id}
                label={tagName}
                group={groupName}
              >
                <Space>
                  <TagsOutlined />
                  {tagName}
                  {groupName && (
                    <Text type="secondary">({groupName})</Text>
                  )}
                </Space>
              </Option>
            );
          })}
        </OptGroup>
      ))}
    </Select>
  );
};

export default TagSelect;


